import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';

import ActionButton from '../../../components/ActionButton/ActionButton';
import { customPalette } from '../../../../styles/theme';
import GeneralDocumentBannerCreateModal from '../GeneralDocumentBannerCreateModal/GeneralDocumentBannerCreateModal';
import GeneralDocumentBanner from '../GeneralDocumentBanner/GeneralDocumentBanner';
import { GeneralDocumentWidgetParent } from '../../../../stores/generalDocumentsWidgetStore';
import GeneralDocumentListCreateModal from '../GeneralDocumentListCreateModal/GeneralDocumentListCreateModal';
import GeneralDocumentsList from '../GeneralDocumentsList/GeneralDocumentsLists';
import {
  cropConnectApiClient,
  CropConnectRoleDefinitions,
  WidgetDto,
} from '../../../../services/cropConnectApiClient';
import { useI18n } from '../../../../hooks/useI18n';
import { TranslationKeys } from '../../../../i18n/locales/defaultResources';
import WarningModal from '../../../components/WarningModal/WarningModal';
import { useContextStore } from '../../../../stores/contextStore';
import { securityTrimConditionsMet } from '../../../../services/auth/SecurityTrim';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const AdminContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  background-color: ${customPalette.accent.white};
`;

const CenteredH2 = styled.h2`
  text-align: center;
`;

interface GeneralDocumentsComponentProps {
  parent: GeneralDocumentWidgetParent;
}

const GeneralDocumentsComponent: React.FC<GeneralDocumentsComponentProps> = ({
  parent,
}) => {
  const { t } = useI18n();
  const {
    globalAssignment,
    nordzuckerInternalUser,
    businessPartnerAssignment,
    isAccessOnBehalf,
    isContextReady,
  } = useContextStore();
  const [isAdminView, setIsAdminView] = useState(false);
  const [isBannerCreateModalOpen, setBannerCreateModalOpen] = useState(false);
  const [isListCreateModalOpen, setListCreateModalOpen] = useState(false);
  const [selectedWidget, setSelectedWidget] = useState<WidgetDto | null>(null);
  const [widgets, setWidgets] = useState<WidgetDto[]>([]);
  const [newOrder, setNewOrder] = useState<number>(0);
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);

  const handleBannerWidgetEdit = (widget: WidgetDto) => {
    setSelectedWidget(widget);
    setBannerCreateModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const handleListWidgetEdit = (widget: WidgetDto) => {
    setSelectedWidget(widget);
    setListCreateModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const handleDelete = async (widget: WidgetDto, numberOfDocs?: number) => {
    if (
      widget.type === 'GENERAL_DOCUMENT_LIST' &&
      numberOfDocs &&
      numberOfDocs > 0
    ) {
      setIsWarningModalOpen(true);
      return;
    }
    const deletion = await cropConnectApiClient.deleteWidget(
      parent,
      widget.accessId!,
      {
        widgetType: widget.type,
        order: widget.order,
        propertyBag: { headlines: widget.propertyBag?.headlines },
      }
    );
    if (deletion.data) {
      setWidgets((prevWidgets) => {
        const updatedWidgets = prevWidgets.filter(
          (w) => w.accessId !== widget.accessId
        );

        // Reassign order so it stays sequential (0,1,2,...)
        const reorderedWidgets = updatedWidgets.map((widget, index) => ({
          ...widget,
          order: index,
        }));

        saveOrderToAPI(reorderedWidgets);

        return reorderedWidgets;
      });
    }
  };

  const moveWidget = (widgetId: string, direction: 'up' | 'down') => {
    setWidgets((prevWidgets) => {
      const index = prevWidgets.findIndex((w) => w.accessId === widgetId);
      if (index === -1) return prevWidgets;

      const newIndex = direction === 'up' ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= prevWidgets.length) return prevWidgets; // Prevent out-of-bounds move

      // Swap order values
      const updatedWidgets = [...prevWidgets];
      [updatedWidgets[index].order, updatedWidgets[newIndex].order] = [
        updatedWidgets[newIndex].order,
        updatedWidgets[index].order,
      ];

      saveOrderToAPI(updatedWidgets);

      return [...updatedWidgets.sort((a, b) => a.order - b.order)];
    });
  };

  const saveOrderToAPI = async (widgetsToSave: WidgetDto[]) => {
    widgetsToSave.forEach(async (widget) => {
      await cropConnectApiClient.updateWidget(parent, widget.accessId!, {
        widgetType: widget.type,
        order: widget.order,
        propertyBag: widget.propertyBag,
      });
    });
  };

  useEffect(() => {
    if (isContextReady()) {
      setTimeout(() => {
        setIsAdminView(
          !isAccessOnBehalf &&
            securityTrimConditionsMet(
              {
                requiredGlobalRoles: [
                  CropConnectRoleDefinitions.DocumentPublisher,
                ],
              },
              globalAssignment,
              businessPartnerAssignment,
              nordzuckerInternalUser
            )
        );
      }, 2000);
    }
    const fetchWidgets = async () => {
      const widgets = await cropConnectApiClient.getWidgetsForParent(parent);
      if (widgets.data) {
        setWidgets(widgets.data.sort((a, b) => a.order - b.order));
        console.log(widgets);
      }
    };
    fetchWidgets();
  }, [globalAssignment]);

  useEffect(() => {
    setNewOrder(
      widgets.length > 0 ? Math.max(...widgets.map((w) => w.order)) + 1 : 0
    );
    if (isContextReady()) {
      setIsAdminView(
        !isAccessOnBehalf &&
          securityTrimConditionsMet(
            {
              requiredGlobalRoles: [
                CropConnectRoleDefinitions.DocumentPublisher,
              ],
            },
            globalAssignment,
            businessPartnerAssignment,
            nordzuckerInternalUser
          )
      );
    }
  }, [widgets]);

  return (
    <>
      <Container>
        {isAdminView && (
          <AdminContainer>
            <CenteredH2>
              {t(TranslationKeys.general_document_admin_title)}
            </CenteredH2>
            <ActionButton
              variant="accept-blue"
              buttonText={t(
                TranslationKeys.general_document_admin_add_new_document_list_button
              )}
              onClickAction={() => {
                setListCreateModalOpen(true);
                document.body.style.overflow = 'hidden';
              }}
            />
            <ActionButton
              variant="accept-blue"
              buttonText={t(
                TranslationKeys.general_document_admin_add_new_document_banner_button
              )}
              onClickAction={() => {
                setBannerCreateModalOpen(true);
                document.body.style.overflow = 'hidden';
              }}
            />
          </AdminContainer>
        )}
        {widgets.map((widget) => {
          if (widget.type === 'GENERAL_DOCUMENT_LIST') {
            return (
              <GeneralDocumentsList
                key={widget.accessId}
                listWidget={widget}
                onMoveUp={() => moveWidget(widget.accessId!, 'up')}
                onMoveDown={() => moveWidget(widget.accessId!, 'down')}
                onEdit={() => handleListWidgetEdit(widget)}
                onDelete={(numberOfDocs) => handleDelete(widget, numberOfDocs)}
              />
            );
          } else {
            return (
              <GeneralDocumentBanner
                key={widget.accessId}
                bannerWidget={widget}
                onMoveUp={() => moveWidget(widget.accessId!, 'up')}
                onMoveDown={() => moveWidget(widget.accessId!, 'down')}
                onEdit={() => handleBannerWidgetEdit(widget)}
                onDelete={() => handleDelete(widget)}
              />
            );
          }
        })}
      </Container>
      <GeneralDocumentBannerCreateModal
        parent={parent}
        widget={selectedWidget}
        isOpen={isBannerCreateModalOpen}
        newOrder={newOrder}
        onCreateSuccess={(newWidget: WidgetDto) =>
          setWidgets([...widgets, newWidget].sort((a, b) => a.order - b.order))
        }
        onUpdateSuccess={(updatedWidget: WidgetDto) =>
          setWidgets(
            widgets.map((widget) =>
              widget.accessId === updatedWidget.accessId
                ? updatedWidget
                : widget
            )
          )
        }
        onClose={() => {
          setSelectedWidget(null);
          setBannerCreateModalOpen(false);
          document.body.style.overflow = 'overlay';
        }}
      />
      <GeneralDocumentListCreateModal
        parent={parent}
        widget={selectedWidget}
        isOpen={isListCreateModalOpen}
        newOrder={newOrder}
        onCreateSuccess={(newWidget: WidgetDto) =>
          setWidgets([...widgets, newWidget].sort((a, b) => a.order - b.order))
        }
        onUpdateSuccess={(updatedWidget: WidgetDto) =>
          setWidgets(
            widgets.map((widget) =>
              widget.accessId === updatedWidget.accessId
                ? updatedWidget
                : widget
            )
          )
        }
        onClose={() => {
          setSelectedWidget(null);
          setListCreateModalOpen(false);
          document.body.style.overflow = 'overlay';
        }}
      />
      <WarningModal
        isOpen={isWarningModalOpen}
        onClose={() => setIsWarningModalOpen(false)}
      ></WarningModal>
    </>
  );
};

export default GeneralDocumentsComponent;
